import { mapGetters } from 'vuex';
import { exportCsv } from 'json2csv-export';
import i18n from '@/plugins/i18n';

/* eslint-disable import/prefer-default-export */
export const businessPassenger = {
  data() {
    return {
      searchObject: {
        page: 0,
        maxPerPage: 10,
        orderBy: '-givenName',
        bookingChannelId: null,
        passengerId: null,
        businessPassengerGroupId: null,
        businessAccountIds: [],
        dynamicSearch: null,
        relations: 'businessPassengerGroups',
      },
      searchGuestObject: {
        page: 0,
        maxPerPage: 10,
        orderBy: '-givenName',
        bookingChannelId: null,
        passengerId: null,
        businessPassengerGroupId: null,
        businessAccountIds: [],
        dynamicSearch: null,
      },
      searchPassengerGroupsObject: {
        page: 0,
        maxPerPage: 10,
        sort: '-createdAt',
        dynamicSearch: null,
        ids: null,
        corporationId: null,
        type: null,
        restrictionId: null,
        employeeId: null,
      },
      businessPassengers: [],
      businessPassengerGroups: [],
      guestPassengers: [],
      totalBusinessPassengerGroups: 0,
      totalBusinessPassengers: 0,
      totalGuestPassengers: 0,
      businessPassengersLoading: false,
      guestPassengersLoading: false,
    };
  },

  computed: {
    ...mapGetters('corporations', ['getSelectedCorporationId']),
  },

  watch: {
    getSelectedCorporationId(val) {
      if (!val) return;
      const { tab } = this.$route.query;

      this.initCallPassengers(tab);
    },
    searchObject: {
      handler() {
        this.getBusinessPassengers();
      },
      deep: true,
    },
    searchGuestObject: {
      handler() {
        this.getBusinessGuests();
      },
      deep: true,
    },
    searchPassengerGroupsObject: {
      handler() {
        this.getBusinessPassengerGroups();
      },
      deep: true,
    },
  },

  methods: {
    async getBusinessPassengers() {
      if (!this.getSelectedCorporationId) return;
      this.businessPassengersLoading = true;
      const result = await this.$store.dispatch(
        'businessPassenger/getBusinessPassengerList',
        { ...this.searchObject, businessAccountIds: [this.getSelectedCorporationId] },
      );

      if (!result) return;

      this.businessPassengers = result.results;
      this.totalBusinessPassengers = result.total;
      this.businessPassengersLoading = false;
    },

    async getBusinessGuests() {
      if (!this.getSelectedCorporationId) return;

      this.guestPassengersLoading = true;

      const result = await this.$store.dispatch(
        'businessGuest/getBusinessGuestList',
        { ...this.searchGuestObject, businessAccountIds: [this.getSelectedCorporationId] },
      );

      if (!result) return;

      this.guestPassengers = result.results;
      this.totalGuestPassengers = result.total;
      this.guestPassengersLoading = false;
    },

    async getBusinessPassengerGroups() {
      const result = await this.$store.dispatch(
        'employee/listEmployeeGroups',
        { ...this.searchPassengerGroupsObject, corporationId: this.getSelectedCorporationId },
      );

      if (!result) return;

      this.businessPassengerGroups = result.data;
      this.totalBusinessPassengerGroups = Number(result.headers['x-total-count'] || 0);
    },

    initCallPassengers(tab = null) {
      if (tab === 'employee') {
        this.getBusinessPassengers();
      } else if (tab === 'guest') {
        this.getBusinessGuests();
      } else if (tab === 'groups') {
        this.getBusinessPassengerGroups();
      }
    },

    async loadActiveCorporationGroups(businessAccountIds) {
      this.searchPassengerGroupsObject.businessAccountIds = [businessAccountIds];
      await this.getBusinessPassengerGroups();
    },
  },
};

export const exportPassengers = {
  methods: {
    async exportUsersList() {
      const header = {
        id: 'id',
        givenName: i18n.t('usersExport.headers.givenName'),
        familyName: i18n.t('usersExport.headers.familyName'),
        email: i18n.t('usersExport.headers.email'),
        phoneNumber: i18n.t('usersExport.headers.phoneNumber'),
        userType: i18n.t('usersExport.headers.userType'),
        businessAccountId: i18n.t('usersExport.headers.businessAccountId'),
        businessAccountName: i18n.t('usersExport.headers.businessAccountName'),
        createdAt: i18n.t('usersExport.headers.createdAt'),
      };
      const businessPassengers = await this.getExportBusinessPassengers(0, 50, [this.getSelectedCorporationId], []);
      const businessGuests = await this.getExportBusinessGuests(0, 50, this.getSelectedCorporationId, []);
      const mappedBusinessPassengers = [];
      businessPassengers.forEach((passenger) => {
        const mappedPassenger = {
          id: passenger.id,
          givenName: passenger.passenger.givenName,
          familyName: passenger.passenger.familyName,
          email: passenger.email || passenger.passenger.email,
          phoneNumber: `${passenger.passenger.phoneNumber}`,
          userType: 'BUSINESS_PASSENGER',
          businessAccountId: passenger.businessAccountId,
          businessAccountName: passenger.businessAccount.name,
          createdAt: passenger.createdAt,
        };
        mappedBusinessPassengers.push(mappedPassenger);
      });
      const mappedBusinessGuests = [];
      businessGuests.forEach((guest) => {
        const mappedGuest = {
          id: guest.id,
          givenName: guest.givenName,
          familyName: guest.familyName,
          email: guest.email,
          phoneNumber: guest.phoneNumber,
          userType: 'BUSINESS_GUEST',
          businessAccountId: guest.businessAccountId,
          businessAccountName: guest.businessAccount.name,
          createdAt: guest.createdAt,
        };
        mappedBusinessGuests.push(mappedGuest);
      });
      const data = [
        ...mappedBusinessPassengers,
        ...mappedBusinessGuests,
      ];
      const { businessAccountName } = data[0];
      const fileName = (businessAccountName) ? `${businessAccountName}-users-export` : 'users-export';
      exportCsv({
        header,
        data,
        filename: fileName.replace(/\s+/g, ''), // file name
      });
    },
    async getExportBusinessPassengers(page, maxPerPage, businessAccountIds, allPassengers) {
      const passengers = await this.getBusinessPassengersForPage(page, maxPerPage, businessAccountIds, allPassengers);
      const accumulation = [...allPassengers, ...passengers.results];
      if ((passengers.page + 1) * maxPerPage < passengers.total) {
        // eslint-disable-next-line no-underscore-dangle
        return this.getExportBusinessPassengers(page + 1, maxPerPage, businessAccountIds, accumulation);
      }
      return accumulation;
    },
    async getBusinessPassengersForPage(page, maxPerPage, businessAccountIds) {
      const payload = {
        page,
        maxPerPage,
        businessAccountIds,
      };
      const businessPassengers = await this.$store.dispatch('businessPassenger/getBusinessPassengerList', payload);
      return businessPassengers;
    },
    async getExportBusinessGuests(page, maxPerPage, businessAccountId, allPassengers) {
      const passengers = await this.getBusinessGuestsForPage(page, maxPerPage, businessAccountId, allPassengers);
      const accumulation = [...allPassengers, ...passengers.results];
      if ((passengers.page + 1) * maxPerPage < passengers.total) {
        return this.getExportBusinessGuests(page + 1, maxPerPage, businessAccountId, accumulation);
      }
      return accumulation;
    },
    async getBusinessGuestsForPage(page, maxPerPage, businessAccountId) {
      const payload = {
        page,
        maxPerPage,
        businessAccountId,
      };
      const businessGuests = await this.$store.dispatch('businessGuest/getBusinessGuestList', payload);
      return businessGuests;
    },
  },
};
